/**
 * Audio Manager for Homerun Game
 *
 * Provides centralized audio playback management with:
 * - Preloading for instant playback
 * - Volume control per category
 * - Mute/unmute functionality
 * - Browser autoplay policy handling
 * - Audio pooling for overlapping sounds
 */

import {
  SoundEffect,
  ALL_SOUNDS,
  DEFAULT_VOLUME_SETTINGS,
  getSoundById,
} from '@/config/soundEffects';
import logger from '@/utils/logger';

interface VolumeSettings {
  master: number;
  game: number;
  player: number;
  items: number;
  ui: number;
}

class AudioManager {
  private audioCache: Map<string, HTMLAudioElement[]> = new Map();
  private volumeSettings: VolumeSettings = { ...DEFAULT_VOLUME_SETTINGS };
  private isMuted: boolean = false;
  private isInitialized: boolean = false;
  private poolSize: number = 3; // Number of instances per sound for overlapping playback

  /**
   * Initialize the audio manager and preload sounds
   * Call this on user interaction to comply with browser autoplay policies
   */
  async initialize(soundsToPreload: SoundEffect[] = ALL_SOUNDS): Promise<void> {
    if (this.isInitialized) {
      logger.debug('Audio manager already initialized');
      return;
    }

    try {
      // Load volume settings from localStorage if available
      this.loadVolumeSettings();

      // Preload all sounds
      const preloadPromises = soundsToPreload.map(sound => this.preloadSound(sound));
      await Promise.allSettled(preloadPromises);

      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize audio manager:', error);
      throw error;
    }
  }

  /**
   * Preload a single sound effect with audio pooling
   */
  private async preloadSound(sound: SoundEffect): Promise<void> {
    if (this.audioCache.has(sound.id)) {
      return; // Already preloaded
    }

    const audioPool: HTMLAudioElement[] = [];

    for (let i = 0; i < this.poolSize; i++) {
      const audio = new Audio(sound.path);
      audio.preload = 'auto';
      audio.volume = this.calculateVolume(sound);

      // Handle loading
      await new Promise<void>((resolve, reject) => {
        audio.addEventListener('canplaythrough', () => resolve(), { once: true });
        audio.addEventListener('error', () => {
          logger.debug('Failed to preload sound', { soundId: sound.id, path: sound.path });
          reject(new Error(`Failed to load ${sound.id}`));
        });

        // Timeout after 5 seconds
        setTimeout(() => resolve(), 5000);
      });

      audioPool.push(audio);
    }

    this.audioCache.set(sound.id, audioPool);
  }

  /**
   * Play a sound effect by ID
   * Returns a promise that resolves when playback starts
   */
  async play(soundId: string): Promise<void> {
    if (this.isMuted) {
      return;
    }

    const audioPool = this.audioCache.get(soundId);
    if (!audioPool || audioPool.length === 0) {
      logger.debug('Skipped sound playback because sound is not loaded', { soundId });
      return;
    }

    // Find an available audio instance (not currently playing)
    let audio = audioPool.find(a => a.paused);

    // If all instances are playing, use the first one and restart it
    if (!audio) {
      audio = audioPool[0];
      audio.currentTime = 0;
    }

    try {
      // Update volume in case settings changed
      const sound = getSoundById(soundId);
      if (sound) {
        audio.volume = this.calculateVolume(sound);
      }

      await audio.play();
    } catch (error) {
      // Handle autoplay restrictions
      if (error instanceof Error && error.name === 'NotAllowedError') {
        console.warn('🔇 Autoplay blocked by browser. User interaction required.');
      } else {
        console.error(`❌ Failed to play sound: ${soundId}`, error);
      }
    }
  }

  /**
   * Stop a currently playing sound
   */
  stop(soundId: string): void {
    const audioPool = this.audioCache.get(soundId);
    if (audioPool) {
      audioPool.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll(): void {
    this.audioCache.forEach((audioPool) => {
      audioPool.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    });
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setMasterVolume(volume: number): void {
    this.volumeSettings.master = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
    this.saveVolumeSettings();
  }

  /**
   * Set category volume (0.0 to 1.0)
   */
  setCategoryVolume(category: keyof Omit<VolumeSettings, 'master'>, volume: number): void {
    this.volumeSettings[category] = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
    this.saveVolumeSettings();
  }

  /**
   * Get current volume settings
   */
  getVolumeSettings(): VolumeSettings {
    return { ...this.volumeSettings };
  }

  /**
   * Mute all sounds
   */
  mute(): void {
    this.isMuted = true;
    this.updateAllVolumes();
  }

  /**
   * Unmute all sounds
   */
  unmute(): void {
    this.isMuted = false;
    this.updateAllVolumes();
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.isMuted;
  }

  /**
   * Check if audio is muted
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Calculate effective volume for a sound effect
   */
  private calculateVolume(sound: SoundEffect): number {
    if (this.isMuted) return 0;

    const categoryVolume = this.volumeSettings[sound.category];
    const soundVolume = sound.volume ?? 1.0;
    const masterVolume = this.volumeSettings.master;

    return masterVolume * categoryVolume * soundVolume;
  }

  /**
   * Update volumes for all cached audio elements
   */
  private updateAllVolumes(): void {
    this.audioCache.forEach((audioPool, soundId) => {
      const sound = getSoundById(soundId);
      if (sound) {
        const newVolume = this.calculateVolume(sound);
        audioPool.forEach(audio => {
          audio.volume = newVolume;
        });
      }
    });
  }

  /**
   * Save volume settings to localStorage
   */
  private saveVolumeSettings(): void {
    try {
      localStorage.setItem('homerun_volume_settings', JSON.stringify(this.volumeSettings));
    } catch (error) {
      console.warn('⚠️ Failed to save volume settings:', error);
    }
  }

  /**
   * Load volume settings from localStorage
   */
  private loadVolumeSettings(): void {
    try {
      const saved = localStorage.getItem('homerun_volume_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.volumeSettings = { ...DEFAULT_VOLUME_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn('⚠️ Failed to load volume settings, using defaults:', error);
    }
  }

  /**
   * Reset all volume settings to defaults
   */
  resetVolumeSettings(): void {
    this.volumeSettings = { ...DEFAULT_VOLUME_SETTINGS };
    this.updateAllVolumes();
    this.saveVolumeSettings();
  }

  /**
   * Check if audio manager is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get initialization status and loaded sound count
   */
  getStatus(): {
    initialized: boolean;
    loadedSounds: number;
    totalSounds: number;
    muted: boolean;
  } {
    return {
      initialized: this.isInitialized,
      loadedSounds: this.audioCache.size,
      totalSounds: ALL_SOUNDS.length,
      muted: this.isMuted,
    };
  }
}

// Export singleton instance
export const audioManager = new AudioManager();

// Export convenience functions
export const playSound = (soundId: string) => audioManager.play(soundId);
export const stopSound = (soundId: string) => audioManager.stop(soundId);
export const stopAllSounds = () => audioManager.stopAll();
export const initAudio = () => audioManager.initialize();
export const muteAudio = () => audioManager.mute();
export const unmuteAudio = () => audioManager.unmute();
export const toggleAudioMute = () => audioManager.toggleMute();
